import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../App";
import SearchBox from "../components/SearchBox";
import CarCard from "../components/CarCard";
import CategoryGrid from "../components/CategoryGrid";
import CategoryCarousel from "../components/CategoryCarousel";
import CategoryNav from "../components/CategoryNav";
import { Button } from "../components/ui/button";
import { ArrowRight, Shield, Clock, Star, Users, ChevronRight, Play } from "lucide-react";
import PremiumIcon from "../components/ui/PremiumIcon";
import useSeo from "../hooks/useSeo";
import useCmsContent from "../hooks/useCmsContent";

const HOMEPAGE_DEFAULTS = {
  hero_title: "Renta autos entre particulares en México – rápido, seguro y sin papeleo",
  hero_subtitle: "La forma más fácil de rentar un auto o ganar dinero con el tuyo. Sin filas, sin contratos complicados. Todo desde tu celular.",
  hero_cta_label: "Buscar un auto",
  hero_cta_link: "/search",
};

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith("en");
  const { content: cms } = useCmsContent("homepage", HOMEPAGE_DEFAULTS);
  // CMS text is authored in Spanish; in English we prefer the optional
  // *_en CMS fields and fall back to the translated defaults.
  const heroTitle = isEn ? cms.hero_title_en || t("home.heroTitle") : cms.hero_title;
  const heroSubtitle = isEn ? cms.hero_subtitle_en || t("home.heroSubtitle") : cms.hero_subtitle;
  const heroCtaLabel = isEn ? cms.hero_cta_label_en || t("home.heroCta") : cms.hero_cta_label;
  useSeo({
    title: "Renta de autos en México · Rivones — rápido, seguro y sin papeleo",
    description:
      "Renta de autos entre particulares en México. Reserva por hora, día o semana desde tu celular, o publica tu auto y genera ingresos. Disponible en Tijuana y próximamente en todo México.",
    keywords:
      "renta de autos, renta de autos en México, renta de autos en Tijuana, renta de autos entre particulares, alquiler de autos, rentar auto por día, rentar un auto, publicar mi auto, ganar dinero con mi auto, Rivones",
    canonical: "https://rivones.com/",
  });
  const [featuredCars, setFeaturedCars] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // We fetch BOTH endpoints in parallel: the featured strip keeps working
    // even if the categories endpoint is empty (e.g. while the DB is being
    // seeded), and the new category carousels render below the hero as soon
    // as data arrives.
    const fetchHome = async () => {
      try {
        const [featuredRes, catRes] = await Promise.all([
          api.get("/vehicles?limit=8"),
          api.get("/vehicles/categories?city=Tijuana&per_category=12"),
        ]);
        setFeaturedCars(featuredRes.data?.vehicles || []);
        setCategories(catRes.data?.categories || []);
      } catch (error) {
        console.error("Error fetching homepage data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchHome();
  }, []);

  const features = [
    {
      icon: Shield,
      tone: "blue",
      title: t("home.feat1Title"),
      description: t("home.feat1Body"),
    },
    {
      icon: Star,
      tone: "amber",
      title: t("home.feat2Title"),
      description: t("home.feat2Body"),
    },
    {
      icon: Clock,
      tone: "emerald",
      title: t("home.feat3Title"),
      description: t("home.feat3Body"),
    },
    {
      icon: Users,
      tone: "terracotta",
      title: t("home.feat4Title"),
      description: t("home.feat4Body"),
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Hero Section - Airbnb Style */}
      <section className="relative bg-[#FAFAFA] overflow-hidden">
        <div className="container-main py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-white border border-neutral-200 rounded-full px-4 py-2 text-sm" data-testid="hero-trust-line">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-neutral-600">
                  {t("home.badgeAvailability")}
                </span>
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-[#111111] leading-[1.1] tracking-tight"
                data-testid="hero-h1"
              >
                {heroTitle}
              </h1>

              <p className="text-lg text-neutral-600 max-w-xl leading-relaxed" data-testid="hero-subheadline">
                {heroSubtitle}
              </p>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => navigate(cms.hero_cta_link || "/search")}
                  className="btn-primary text-base"
                  data-testid="hero-search-cta"
                >
                  {heroCtaLabel}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate("/host/listings?new=1")}
                  variant="outline"
                  className="text-base h-11 rounded-xl border-neutral-300 hover:bg-neutral-50"
                  data-testid="hero-list-cta"
                >
                  {t("home.heroListCta")}
                </Button>
              </div>
              
              {/* Stats */}
              <div className="flex gap-8 pt-4">
                <div>
                  <p className="text-2xl font-heading font-bold text-[#111111]">2,000+</p>
                  <p className="text-sm text-neutral-500">{t("home.statTrips")}</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-[#111111]">4.9</p>
                  <p className="text-sm text-neutral-500">{t("home.statRating")}</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-[#111111]">100%</p>
                  <p className="text-sm text-neutral-500">{t("home.statHosts")}</p>
                </div>
              </div>
            </div>
            
            {/* Right Image Grid - Bento Style */}
            <div className="relative hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                    <img
                      src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=600"
                      alt="Car 1"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden aspect-square">
                    <img
                      src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400"
                      alt="Car 2"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden aspect-square">
                    <img
                      src="https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=400"
                      alt="Car 3"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                    <img
                      src="https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=600"
                      alt="Car 4"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
              
              {/* Floating Card */}
              <div className="absolute -left-6 bottom-20 bg-white rounded-2xl p-4 shadow-hover border border-neutral-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Star className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#111111]">{t("home.ratingCardStars")}</p>
                    <p className="text-sm text-neutral-500">{t("home.ratingCardReviews")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Search Box */}
        <div className="container-main pb-8 -mt-4">
          <SearchBox />
        </div>
      </section>

      {/* Sticky category nav — chips below the search bar. Auto-highlights
          the section currently in view and smooth-scrolls when tapped. */}
      {categories.length > 0 && <CategoryNav categories={categories} />}

      {/* Vehicle category carousels — Netflix/Airbnb-style horizontal scroll.
          Categories with fewer than 3 vehicles are filtered out server-side
          so we don't need any client guard here. */}
      <CategoryGrid />
      {loading ? (
        <section className="py-10">
          <div className="container-main">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-neutral-200 rounded-2xl mb-3" />
                  <div className="h-5 bg-neutral-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-neutral-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        categories.map((cat) => (
          <CategoryCarousel
            key={cat.id}
            id={cat.id}
            title={isEn ? cat.label_en || cat.label_es : cat.label_es}
            subtitle={
              cat.id === "favorites"
                ? t("home.favoritesSubtitle")
                : t("home.vehiclesAvailable", { count: cat.count })
            }
            items={cat.items}
            onSeeAll={() => navigate(`/search?category=${cat.id}`)}
          />
        ))
      )}

      {/* Features Section */}
      <section className="py-20 bg-[#FAFAFA]">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="label-overline mb-2">{t("home.whyOverline")}</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#111111] mb-4">
              {t("home.whyTitle")}
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              {t("home.whySubtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-6 border border-neutral-100 hover:shadow-hover hover:-translate-y-1 transition-all duration-300"
              >
                <PremiumIcon
                  icon={feature.icon}
                  variant="gradient"
                  tone={feature.tone}
                  size="lg"
                  className="mb-4 group-hover:scale-105"
                />
                <h3 className="font-heading font-semibold text-lg text-[#111111] mb-2">
                  {feature.title}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#111111]">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-neutral-400 uppercase tracking-wider text-sm mb-4">{t("home.hostOverline")}</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
                {t("home.hostTitle")}
              </h2>
              <p className="text-neutral-400 text-lg mb-8 leading-relaxed">
                {t("home.hostBody")}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/host/dashboard">
                  <Button className="bg-white text-[#111111] hover:bg-neutral-100 rounded-full px-8 py-3.5 font-semibold" data-testid="become-host-btn">
                    {t("home.hostCta")}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              
              <div className="flex gap-8 mt-10 pt-8 border-t border-neutral-800">
                <div>
                  <p className="text-2xl font-heading font-bold text-white">$15,000+</p>
                  <p className="text-sm text-neutral-500">{t("home.hostStatEarnings")}</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-white">500+</p>
                  <p className="text-sm text-neutral-500">{t("home.hostStatActive")}</p>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:block relative">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800"
                  alt="Host with car"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download the app — pairs with the PWA install prompt. Deep links
          route to the app-store listings once published; today they
          fallback to the current PWA install experience via a query flag
          so the CTAs are never dead. */}
      <section className="py-16 bg-gradient-to-br from-terracotta/5 via-sand to-amber-50" data-testid="download-app-section">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-terracotta uppercase tracking-wider text-sm font-semibold mb-3">
                {t("home.downloadOverline")}
              </p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-agave mb-4">
                {t("home.downloadTitle")}
              </h2>
              <p className="text-neutral-700 text-lg mb-8 leading-relaxed">
                {t("home.downloadBody")}
              </p>
              <div className="flex flex-wrap gap-4" data-testid="download-app-buttons">
                {/* App Store badge — brand-safe rendering. Deep link goes
                    to the App Store listing once approved; today it points
                    at the PWA install flow via `?install=ios`. */}
                <a
                  href="/install?source=app-store"
                  className="inline-flex items-center gap-3 bg-black text-white rounded-2xl pl-4 pr-5 py-3 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  aria-label="Descargar en la App Store"
                  data-testid="download-app-appstore-btn"
                >
                  <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor" aria-hidden>
                    <path d="M17.05 12.04c-.03-2.85 2.33-4.22 2.44-4.29-1.33-1.95-3.4-2.21-4.14-2.24-1.76-.18-3.44 1.04-4.33 1.04-.9 0-2.28-1.01-3.75-.98-1.93.03-3.71 1.12-4.7 2.84-2 3.47-.51 8.59 1.43 11.4.95 1.38 2.08 2.92 3.56 2.87 1.43-.06 1.97-.92 3.69-.92 1.72 0 2.21.92 3.72.89 1.54-.03 2.51-1.4 3.45-2.79 1.09-1.6 1.54-3.15 1.56-3.23-.03-.01-2.99-1.15-3.02-4.55zM14.36 4.06c.79-.95 1.32-2.28 1.17-3.6-1.13.05-2.5.75-3.32 1.7-.73.84-1.37 2.19-1.2 3.49 1.26.1 2.55-.64 3.35-1.59z" />
                  </svg>
                  <div className="text-left leading-tight">
                    <p className="text-[10px] uppercase opacity-80 tracking-wider">{t("home.downloadOn")}</p>
                    <p className="text-lg font-heading font-semibold -mt-0.5">App Store</p>
                  </div>
                </a>
                {/* Google Play badge — brand-safe rendering with the
                    canonical multi-color triangle glyph. */}
                <a
                  href="/install?source=google-play"
                  className="inline-flex items-center gap-3 bg-black text-white rounded-2xl pl-4 pr-5 py-3 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  aria-label="Disponible en Google Play"
                  data-testid="download-app-googleplay-btn"
                >
                  <svg viewBox="0 0 24 24" className="w-8 h-8" aria-hidden>
                    <path fill="#00D4FF" d="M3.6 2.32C3.24 2.68 3 3.19 3 3.83v16.34c0 .64.24 1.16.6 1.51l.07.07L13.02 12l-9.35-9.68z" />
                    <path fill="#FFCE00" d="M17 15.02L14.02 12 17 8.98l3.68 2.09c1.05.59 1.05 1.56 0 2.15L17 15.02z" />
                    <path fill="#00F076" d="M17.15 8.83L3.67 2.32c-.28-.18-.6-.25-.9-.17l9.35 9.68 5.03-3z" />
                    <path fill="#FF3A44" d="M17.15 15.17L12.12 12l-9.35 9.68c.3.08.62.01.9-.17l13.48-6.34z" />
                  </svg>
                  <div className="text-left leading-tight">
                    <p className="text-[10px] uppercase opacity-80 tracking-wider">{t("home.availableOn")}</p>
                    <p className="text-lg font-heading font-semibold -mt-0.5">Google Play</p>
                  </div>
                </a>
              </div>
              <p className="text-xs text-neutral-500 mt-4">
                {t("home.downloadFree")}
              </p>
            </div>

            <div className="hidden md:flex items-center justify-center">
              {/* Simple phone mockup — CSS-only so the section has zero
                  extra image weight. */}
              <div className="relative">
                <div className="w-64 h-[520px] rounded-[2.5rem] bg-neutral-900 shadow-2xl p-3 rotate-3">
                  <div className="w-full h-full rounded-[2rem] bg-gradient-to-br from-terracotta to-amber-600 flex flex-col items-center justify-center text-white p-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 border border-white/30">
                      <span className="font-heading font-black text-3xl">R</span>
                    </div>
                    <p className="font-heading font-bold text-xl mb-2">RivoNes</p>
                    <p className="text-white/80 text-sm">{t("home.phoneTagline")}</p>
                  </div>
                </div>
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-emerald-400/20 blur-2xl" />
                <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-terracotta/20 blur-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
